import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Alert,
  Chip,
  Card,
  CardContent,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as DenyIcon,
  Cancel as CancelIcon,
  EventBusy as RemainderIcon,
  Refresh as RefreshIcon,
  BeachAccess as VacationIcon,
  LocalHospital as SickIcon,
  Person as PersonalIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  getPTORequests,
  getMyPTO,
  createPTORequest,
  approvePTORequest,
  denyPTORequest,
  cancelPTORequest,
  cancelPTORemainder,
  getEmployees,
  getMyManagedEmployees,
} from '../api';

const PTO_TYPES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick' },
  { value: 'personal', label: 'Personal' },
];

// Roles the server lets decide every request (PTO_WHOLE_TENANT_ROLES in server/routes/pto.js): tenant admins only.
const PTO_WHOLE_TENANT_ROLES = ['admin'];
const LIST_SCOPE_TIP = 'Shows your own requests and those of the employees you manage. Admins, payroll admins and anyone with View all employees see everyone. Only an employee\'s supervisor or a tenant admin can approve, deny or cancel; reasons are shown only to the employee, their supervisor and admins.';

const STATUS_COLORS = {
  pending: 'warning',
  approved: 'success',
  denied: 'error',
  cancelled: 'default',
  used: 'info',
};

const TYPE_ICONS = {
  vacation: <VacationIcon fontSize="small" />,
  sick: <SickIcon fontSize="small" />,
  personal: <PersonalIcon fontSize="small" />,
};

function countBusinessDays(startDate, endDate) {
  let count = 0;
  const d = new Date(startDate);
  const end = new Date(endDate);
  while (d <= end) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// 'YYYY-MM-DD' from the server (paid_through): parse at local noon so the day never shifts.
const formatYmd = (ymd) => (ymd ? formatDate(`${ymd}T12:00:00`) : '');

export default function PTORequests() {
  const { isAdmin, isManager, isProgramAdmin, user, hasPermission } = useAuth();
  const [successMsg, showSuccess] = useFeedback(3000);
  const [errorMsg, showError] = useFeedback(5000);
  const isAdminOrManager = isAdmin || isManager || isProgramAdmin;
  const approvesAll = PTO_WHOLE_TENANT_ROLES.includes(user?.role);
  const canReviewPTO = hasPermission('can_approve_pto');
  // Server POST /pto honours employee_id only for these roles (pto.js POST /).
  const canFileForOthers = ['admin', 'program_admin'].includes(user?.role);
  const [managedIds, setManagedIds] = useState(null); // Set of direct-report employee ids; null = not loaded / not needed

  const [requests, setRequests] = useState([]);
  const [myBalance, setMyBalance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({ open: false, request: null, action: '' });
  const [remainderDialog, setRemainderDialog] = useState({ open: false, request: null });
  const [reviewNotes, setReviewNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [form, setForm] = useState({
    pto_type: 'vacation',
    start_date: '',
    end_date: '',
    hours_per_day: 8,
    reason: '',
    employee_id: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (employeeFilter) params.employee_id = employeeFilter;

      if (isAdminOrManager) {
        const [reqRes, empRes, managedRes] = await Promise.all([
          getPTORequests(params),
          getEmployees(),
          approvesAll ? Promise.resolve(null) : getMyManagedEmployees(),
        ]);
        setRequests(reqRes.data);
        setEmployees(empRes.data || []);
        setManagedIds(managedRes ? new Set((managedRes.data || []).map(e => e.employee_id)) : null);
        // Load own balance if employee-linked
        try {
          const myRes = await getMyPTO();
          setMyBalance(myRes.data.balance);
        } catch (e) { /* admin may not have employee record */ }
      } else {
        const myRes = await getMyPTO();
        setRequests(myRes.data.requests);
        setMyBalance(myRes.data.balance);
      }
    } catch (err) {
      setManagedIds(null); // unknown, not "no reports": no empty-state alert
      showError('Failed to load PTO data');
    } finally {
      setLoading(false);
    }
  }, [isAdminOrManager, approvesAll, statusFilter, employeeFilter, showError]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async () => {
    try {
      if (!form.start_date || !form.end_date) {
        showError('Start and end dates are required');
        return;
      }
      if (new Date(form.end_date) < new Date(form.start_date)) {
        showError('End date must be on or after start date');
        return;
      }
      const data = { ...form };
      if (!canFileForOthers || !data.employee_id) delete data.employee_id;
      await createPTORequest(data);
      showSuccess('PTO request submitted');
      setDialogOpen(false);
      setForm({ pto_type: 'vacation', start_date: '', end_date: '', hours_per_day: 8, reason: '', employee_id: '' });
      loadData();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to submit request');
    }
  };

  const handleApprove = async () => {
    try {
      await approvePTORequest(reviewDialog.request.id, reviewNotes);
      showSuccess('PTO request approved');
      setReviewDialog({ open: false, request: null, action: '' });
      setReviewNotes('');
      loadData();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to approve request');
    }
  };

  const handleDeny = async () => {
    try {
      await denyPTORequest(reviewDialog.request.id, reviewNotes);
      showSuccess('PTO request denied');
      setReviewDialog({ open: false, request: null, action: '' });
      setReviewNotes('');
      loadData();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to deny request');
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelPTORequest(id);
      showSuccess('PTO request cancelled');
      loadData();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to cancel request');
    }
  };

  // PATCH /pto/:id/cancel-remainder: the server recomputes everything from the paid slices.
  const handleCancelRemainder = async () => {
    try {
      const res = await cancelPTORemainder(remainderDialog.request.id);
      const msg = `Unpaid remainder cancelled — ${parseFloat(res.data.refunded_hours).toFixed(1)}h returned to the PTO balance`;
      const warnings = [];
      if (res.data.employee_notified === false) warnings.push('The employee could not be notified in HourTrax: tell them directly.');
      if (res.data.employee_inactive) warnings.push('This employee is deactivated: the returned hours may be owed as final pay.');
      if (warnings.length > 0) showError(`${msg}. ${warnings.join(' ')}`);
      else showSuccess(msg);
      setRemainderDialog({ open: false, request: null });
      loadData();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to cancel the unpaid remainder');
    }
  };

  const isOwn = (r) => user?.employeeId != null && r.employee_id === user.employeeId;
  // Mirrors PATCH /pto/:id/approve: direct reports only (never self) unless a built-in admin role.
  const canReviewRow = (r) => r.status === 'pending' && canReviewPTO
    && (approvesAll || (!isOwn(r) && managedIds?.has(r.employee_id) === true));
  // Mirrors PATCH /pto/:id/cancel.
  const canCancelRow = (r) => (r.status === 'pending' || r.status === 'approved')
    && (isOwn(r) || approvesAll
      || ((user?.role === 'manager' || canReviewPTO) && !isOwn(r) && managedIds?.has(r.employee_id) === true));
  // Mirrors PATCH /pto/:id/cancel-remainder (plan 108): a used request with unpaid weekdays after
  // the last paid day; tenant admins for anyone (incl. themselves), otherwise Approve PTO + direct
  // report, never own.
  // Nothing to refund when total_hours was hand-edited down to the paid sum: hide the action (the server answers 400).
  const canCancelRemainderRow = (r) => r.status === 'used' && (r.unpaid_remainder_days || 0) > 0
    && parseFloat(r.total_hours) > parseFloat(r.paid_hours || 0)
    && (approvesAll || (canReviewPTO && !isOwn(r) && managedIds?.has(r.employee_id) === true));
  const pendingRequests = isAdminOrManager ? requests.filter(canReviewRow) : [];
  const otherRequests = requests.filter(r => !canReviewRow(r));
  const businessDays = form.start_date && form.end_date && new Date(form.end_date) >= new Date(form.start_date)
    ? countBusinessDays(form.start_date, form.end_date)
    : 0;
  const totalHours = businessDays * (parseFloat(form.hours_per_day) || 8);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Time Off</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Request Time Off
          </Button>
        </Box>
      </Box>

      {/* Balance Card */}
      {myBalance && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">PTO Balance</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {parseFloat(myBalance.pto_balance || 0).toFixed(1)}h
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Accrual Rate</Typography>
                <Typography variant="body1">
                  {parseFloat(myBalance.pto_accrual_rate || 0).toFixed(1)}h / {(myBalance.pto_accrual_frequency || 'per_pay_period').replace(/_/g, ' ')}
                </Typography>
              </Box>
              {parseFloat(myBalance.pto_max_balance) > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Max Balance</Typography>
                  <Typography variant="body1">{parseFloat(myBalance.pto_max_balance).toFixed(1)}h</Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Filters (admin/manager) */}
      {isAdminOrManager && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="denied">Denied</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="used">Used</MenuItem>
          </TextField>
          <TextField
            select size="small" label="Employee" value={employeeFilter}
            onChange={e => setEmployeeFilter(e.target.value)} sx={{ minWidth: 200 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All Employees</MenuItem>
            {employees.map(emp => (
              <MenuItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {isAdminOrManager && canReviewPTO && !approvesAll && managedIds && managedIds.size === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>No managed employees assigned — contact an admin. You can still request and cancel your own time off here.</Alert>
      )}

      {/* Pending Approval Queue (admin/manager only) */}
      {pendingRequests.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText', borderRadius: '4px 4px 0 0' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Pending Approval ({pendingRequests.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.map(req => (
                  <TableRow key={req.id} hover>
                    <TableCell>{req.first_name} {req.last_name}</TableCell>
                    <TableCell>
                      <Chip icon={TYPE_ICONS[req.pto_type]} label={req.pto_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatDate(req.start_date)} – {formatDate(req.end_date)}</TableCell>
                    <TableCell align="right">{parseFloat(req.total_hours).toFixed(1)}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {req.reason || '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Approve">
                        <IconButton color="success" size="small"
                          onClick={() => setReviewDialog({ open: true, request: req, action: 'approve' })}>
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deny">
                        <IconButton color="error" size="small"
                          onClick={() => setReviewDialog({ open: true, request: req, action: 'deny' })}>
                          <DenyIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* All Requests Table */}
      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {isAdminOrManager ? 'All Requests' : 'My Requests'}
            {isAdminOrManager && (
              <Tooltip title={LIST_SCOPE_TIP}>
                <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </Typography>
        </Box>
        <Divider />
        {(isAdminOrManager ? otherRequests : requests).length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No PTO requests found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {isAdminOrManager && <TableCell>Employee</TableCell>}
                  <TableCell>Type</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Reviewer</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(isAdminOrManager ? otherRequests : requests).map(req => (
                  <TableRow key={req.id} hover>
                    {isAdminOrManager && <TableCell>{req.first_name} {req.last_name}</TableCell>}
                    <TableCell>
                      <Chip icon={TYPE_ICONS[req.pto_type]} label={req.pto_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatDate(req.start_date)} – {formatDate(req.end_date)}</TableCell>
                    <TableCell align="right">{parseFloat(req.total_hours).toFixed(1)}</TableCell>
                    <TableCell>
                      <Chip label={req.status} size="small" color={STATUS_COLORS[req.status] || 'default'} />
                      {req.cancelled_remainder_hours != null && (
                        <Tooltip title={`The unpaid remainder (${parseFloat(req.cancelled_remainder_hours).toFixed(1)}h) was cancelled and returned to the PTO balance. The dates and hours shown are what payroll paid.`}>
                          <Chip label="remainder cancelled" size="small" variant="outlined" color="warning" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {req.reason_hidden ? <Typography component="span" variant="caption" color="text.secondary">Private</Typography> : (req.reason || '—')}
                    </TableCell>
                    <TableCell>{req.reviewer_username || '—'}</TableCell>
                    <TableCell align="center">
                      {canCancelRow(req) && (
                        <Tooltip title="Cancel">
                          <IconButton color="default" size="small" onClick={() => handleCancel(req.id)}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canCancelRemainderRow(req) && (
                        <Tooltip title="Cancel unpaid remainder">
                          <IconButton color="warning" size="small" onClick={() => setRemainderDialog({ open: true, request: req })}>
                            <RemainderIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Time Off</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select label="Type" value={form.pto_type}
              onChange={e => setForm({ ...form, pto_type: e.target.value })} fullWidth
            >
              {PTO_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>

            {canFileForOthers && employees.length > 0 && (
              <TextField
                select label="Employee (optional — defaults to you)" value={form.employee_id}
                onChange={e => setForm({ ...form, employee_id: e.target.value })} fullWidth
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Myself</MenuItem>
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</MenuItem>
                ))}
              </TextField>
            )}

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date" type="date" value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth
              />
              <TextField
                label="End Date" type="date" value={form.end_date}
                onChange={e => setForm({ ...form, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth
              />
            </Stack>

            <TextField
              label="Hours Per Day" type="number" value={form.hours_per_day}
              onChange={e => setForm({ ...form, hours_per_day: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0.5, max: 24, step: 0.5 }}
            />

            {businessDays > 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {businessDays} business day{businessDays !== 1 ? 's' : ''} = <strong>{totalHours}h</strong> total
                {myBalance && (
                  <> (Balance: {parseFloat(myBalance.pto_balance || 0).toFixed(1)}h
                  {totalHours > parseFloat(myBalance.pto_balance || 0) && (
                    <Typography component="span" color="error.main" fontWeight="bold"> — insufficient!</Typography>
                  )}
                  )</>
                )}
              </Alert>
            )}

            <TextField
              label="Reason (optional)" value={form.reason}
              helperText="Do not include medical details — HR will request documentation separately if needed."
              onChange={e => setForm({ ...form, reason: e.target.value })}
              multiline rows={2} fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}
            disabled={!form.start_date || !form.end_date || totalHours <= 0}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog (Approve/Deny) */}
      <Dialog open={reviewDialog.open} onClose={() => { setReviewDialog({ open: false, request: null, action: '' }); setReviewNotes(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewDialog.action === 'approve' ? 'Approve' : 'Deny'} PTO Request
        </DialogTitle>
        <DialogContent>
          {reviewDialog.request && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography>
                <strong>{reviewDialog.request.first_name} {reviewDialog.request.last_name}</strong> —{' '}
                {reviewDialog.request.pto_type} ({parseFloat(reviewDialog.request.total_hours).toFixed(1)}h)
              </Typography>
              <Typography color="text.secondary">
                {formatDate(reviewDialog.request.start_date)} – {formatDate(reviewDialog.request.end_date)}
              </Typography>
              {reviewDialog.request.reason && (
                <Typography variant="body2">Reason: {reviewDialog.request.reason}</Typography>
              )}
              <Divider />
              <TextField
                label="Notes (optional)" value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                multiline rows={2} fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReviewDialog({ open: false, request: null, action: '' }); setReviewNotes(''); }}>
            Cancel
          </Button>
          {reviewDialog.action === 'approve' ? (
            <Button variant="contained" color="success" onClick={handleApprove}>Approve</Button>
          ) : (
            <Button variant="contained" color="error" onClick={handleDeny}>Deny</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Unpaid Remainder Dialog (plan 108) */}
      <Dialog open={remainderDialog.open} onClose={() => setRemainderDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Cancel Unpaid Remainder
          <Tooltip title="Use this when someone came back early from time off a payroll run has already partly paid. The days after the last paid day are cancelled and their hours return to the PTO balance; paid days are never taken back here. Use it only after the employee has told you they are back or asked to shorten the request (29 CFR 825.311(c)); do not use it to end leave the employee is still taking. If they took some of the cancelled days, file a new request for just those days afterwards.">
            <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          {remainderDialog.request && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{remainderDialog.request.first_name} {remainderDialog.request.last_name}</strong> —{' '}
                {remainderDialog.request.pto_type} ({parseFloat(remainderDialog.request.total_hours).toFixed(1)}h),{' '}
                {formatDate(remainderDialog.request.start_date)} – {formatDate(remainderDialog.request.end_date)}
              </Typography>
              <Typography variant="body2">
                Paid: {parseFloat(remainderDialog.request.paid_hours || 0).toFixed(1)}h through {formatYmd(remainderDialog.request.paid_through)}
              </Typography>
              <Typography variant="body2">
                Unpaid remainder: {remainderDialog.request.unpaid_remainder_days} weekday{remainderDialog.request.unpaid_remainder_days === 1 ? '' : 's'} after {formatYmd(remainderDialog.request.paid_through)},{' '}
                {(parseFloat(remainderDialog.request.total_hours) - parseFloat(remainderDialog.request.paid_hours || 0)).toFixed(1)}h — returned to the PTO balance.
                The request will end on {formatYmd(remainderDialog.request.paid_through)} and stay marked used.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use only after the employee has told you they are back or asked to shorten the request (29 CFR 825.311(c)). Do not use it to end leave the employee is still taking.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemainderDialog({ open: false, request: null })}>Close</Button>
          <Button variant="contained" color="warning" onClick={handleCancelRemainder}>Cancel Remainder</Button>
        </DialogActions>
      </Dialog>

      {successMsg && <Alert severity="success" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>{errorMsg}</Alert>}
    </Box>
  );
}
